class PackBuilder {
  constructor() {
    // Initialize state
    this.totalUnderwearItems = 0;
    this.totalSwimwearItems = 0;
    this.totalBundlePrice = 0;
    this.bundleDiscountAmountUnderwear = 0;
    this.bundleDiscountAmountSwimwear = 0;
    this.bundleItems = [];

    // Cache DOM elements that don't change
    this.packBuilder = document.querySelector('[data-pack-builder]');

    if (!this.packBuilder) {
      return;
    }

    this.sectionSettingsJson = document.querySelector('[data-pack-builder-settings-json]');
    this.sectionSettings = this.sectionSettingsJson ? JSON.parse(this.sectionSettingsJson.textContent) : null;

    // Initialize
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.updateUI();
  }

  setupEventListeners() {
    // Use event delegation for better performance and automatic handling of new elements
    document.addEventListener('click', (e) => {
      // Handle bundle add buttons
      if (e.target.matches('[data-add-to-bundle]')) {
        e.preventDefault();
        this.handleAddToBundle(e.target);
      }

      // Handle bundle collapse
      if (e.target.matches('[data-bundle-collapse]') || e.target.closest('[data-bundle-collapse]')) {
        e.preventDefault();
        this.handleBundleCollapse();
      }

      // Handle add bundle to cart
      if (e.target.matches('[data-add-bundle]')) {
        e.preventDefault();
        this.addBundleToCart();
      }

      // Handle remove bundle item
      if (e.target.matches('[data-remove-bundle-item]') || e.target.closest('[data-remove-bundle-item]')) {
        e.preventDefault();
        const button = e.target.matches('[data-remove-bundle-item]')
          ? e.target
          : e.target.closest('[data-remove-bundle-item]');
        const index = parseInt(button.getAttribute('data-remove-bundle-item'), 10);
        this.removeFromBundle(index);
      }
    });

    // Handle quantity changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-bundle-product-quantity]')) {
        const index = parseInt(e.target.getAttribute('data-index'), 10);
        const newQuantity = parseInt(e.target.value, 10);
        this.updateQuantity(index, newQuantity);
      }
    });
  }

  handleAddToBundle(button) {
    const variantId = button.dataset.variantId;
    const addedProductType = button.dataset.productType;
    const productPrice = button.dataset.productPrice;
    const featuredImage = button.dataset.featuredImage;
    const productName = button.dataset.productName;

    // Add to global array (for compatibility)
    window.addedProducts = window.addedProducts || [];
    window.addedProducts.push({
      id: variantId,
      productType: addedProductType,
      price: productPrice,
      productImage: featuredImage,
      name: productName,
    });

    // Find the product data
    const matchingProduct = window.addedProducts.find((product) => product.id == variantId);

    if (!matchingProduct) {
      console.error(`No product data found for variant ID ${variantId}`);
      return;
    }

    // Add to bundle
    this.addToBundle(variantId, matchingProduct, addedProductType);

    // Update counters
    if (addedProductType === 'underwear') {
      this.totalUnderwearItems++;
    } else if (addedProductType === 'swimwear') {
      this.totalSwimwearItems++;
    }

    // Check discounts and update UI
    this.checkDiscountEligibility(addedProductType, productPrice);
    this.updateUI();
  }

  addToBundle(variantId, productData, productType) {
    const existingItemIndex = this.bundleItems.findIndex((item) => item.id === variantId);

    if (existingItemIndex !== -1) {
      // Increment quantity if item already exists
      this.bundleItems[existingItemIndex].quantity += 1;
    } else {
      // Add new item
      this.bundleItems.push({
        id: variantId,
        quantity: 1,
        variantData: JSON.parse(JSON.stringify(productData)),
        productType: productType,
        productImage: productData.productImage || productData.featured_image,
      });
    }
  }

  removeFromBundle(index) {
    const itemToRemove = this.bundleItems[index];

    if (!itemToRemove) return;

    const itemType = itemToRemove.productType.toLowerCase();

    // Update counters
    if (itemType === 'underwear') {
      this.totalUnderwearItems -= itemToRemove.quantity;
    } else if (itemType === 'swimwear') {
      this.totalSwimwearItems -= itemToRemove.quantity;
    }

    // Remove from array
    this.bundleItems.splice(index, 1);

    // Reset discounts and recalculate
    this.resetDiscounts();
    this.recalculateAllDiscounts();
    this.updateUI();
  }

  updateQuantity(index, newQuantity) {
    const item = this.bundleItems[index];

    if (!item) return;

    const oldQuantity = item.quantity;
    item.quantity = newQuantity;

    // Update counters
    if (item.productType === 'underwear') {
      this.totalUnderwearItems = this.totalUnderwearItems - oldQuantity + newQuantity;
    } else if (item.productType === 'swimwear') {
      this.totalSwimwearItems = this.totalSwimwearItems - oldQuantity + newQuantity;
    }

    // Recalculate discounts and update UI
    this.resetDiscounts();
    this.recalculateAllDiscounts();
    this.updateUI();
  }

  checkDiscountEligibility(addedProductType, productPrice) {
    const discountSettings = this.getDiscountSettings();

    // Filter settings that match the product type
    const matchingSettings = discountSettings.filter((setting) => {
      const discountType = setting.type.replace('discount-', '');
      return addedProductType && addedProductType === discountType;
    });

    if (matchingSettings.length > 0) {
      // Sort by required products (descending) to get best discount first
      const sortedSettings = matchingSettings.sort((a, b) => {
        return parseInt(b.totalProducts, 10) - parseInt(a.totalProducts, 10);
      });

      const totalItems = addedProductType === 'underwear' ? this.totalUnderwearItems : this.totalSwimwearItems;

      // Find the best applicable discount
      for (const setting of sortedSettings) {
        const requiredProducts = parseInt(setting.totalProducts, 10);

        if (totalItems >= requiredProducts) {
          this.applyDiscount(setting);
          break;
        }
      }
    }

    this.updateBundlePrice();
  }

  applyDiscount(discountSetting) {
    if (discountSetting.type === 'discount-underwear') {
      this.bundleDiscountAmountUnderwear = parseFloat(discountSetting.discountAmount + '00');
      const bundleDiscountText1 = document.querySelector('[data-bundle-discount-text-1]');
      if (bundleDiscountText1 && discountSetting.displayText1) {
        bundleDiscountText1.textContent = discountSetting.displayText1;
      }
    }

    if (discountSetting.type === 'discount-swimwear') {
      this.bundleDiscountAmountSwimwear = parseFloat(discountSetting.discountAmount + '00');
      const bundleDiscountText2 = document.querySelector('[data-bundle-discount-text-2]');
      if (bundleDiscountText2 && discountSetting.displayText1) {
        bundleDiscountText2.textContent = discountSetting.displayText1;
      }
    }
  }

  resetDiscounts() {
    this.bundleDiscountAmountUnderwear = 0;
    this.bundleDiscountAmountSwimwear = 0;

    // Reset discount text displays
    const bundleDiscountText1 = document.querySelector('[data-bundle-discount-text-1]');
    const bundleDiscountText2 = document.querySelector('[data-bundle-discount-text-2]');

    if (bundleDiscountText1) {
      bundleDiscountText1.textContent = this.sectionSettings?.discountText1 || 'Add 1 pair';
    }
    if (bundleDiscountText2) {
      bundleDiscountText2.textContent = this.sectionSettings?.discountText2 || '';
    }
  }

  recalculateAllDiscounts() {
    if (this.totalUnderwearItems > 0) {
      const underwearItem = this.bundleItems.find((item) => item.productType === 'underwear');
      if (underwearItem) {
        this.checkDiscountEligibility('underwear', underwearItem.variantData.price);
      }
    }

    if (this.totalSwimwearItems > 0) {
      const swimwearItem = this.bundleItems.find((item) => item.productType === 'swimwear');
      if (swimwearItem) {
        this.checkDiscountEligibility('swimwear', swimwearItem.variantData.price);
      }
    }
  }

  updateBundlePrice() {
    // Reset and recalculate total price
    this.totalBundlePrice = 0;

    this.bundleItems.forEach((item) => {
      if (item.variantData && item.variantData.price) {
        this.totalBundlePrice += parseFloat(item.variantData.price) * item.quantity;
      }
    });

    const bundlePriceElement = document.querySelector('[data-bundle-price]');
    const bundleDiscountedPriceElement = document.querySelector('[data-bundle-price-discounted]');

    const discountedBundlePrice =
      this.totalBundlePrice - (this.bundleDiscountAmountUnderwear + this.bundleDiscountAmountSwimwear);

    if (bundleDiscountedPriceElement && bundlePriceElement && window.theme) {
      bundlePriceElement.textContent = window.theme.formatMoney(this.totalBundlePrice, window.theme.moneyFormat);
      bundleDiscountedPriceElement.textContent = window.theme.formatMoney(
        discountedBundlePrice,
        window.theme.moneyFormat
      );
    }

    // Show/hide original price based on discount
    if (discountedBundlePrice < this.totalBundlePrice) {
      bundlePriceElement?.classList.remove('hidden');
    } else {
      bundlePriceElement?.classList.add('hidden');
    }
  }

  updateBundleProductsHtml() {
    const bundleProductsContainer = document.querySelector('[data-bundle-products-container]');

    if (!bundleProductsContainer) return;

    // Clear container
    bundleProductsContainer.innerHTML = '';

    // Create product cards
    this.bundleItems.forEach((item, index) => {
      const productData = item.variantData;
      if (!productData) return;

      const productCard = document.createElement('div');
      productCard.classList.add('bundle-product-card');
      productCard.setAttribute('data-bundle-product-index', index);

      const imageUrl = item.productImage;

      productCard.innerHTML = `
        <div class="bundle-product-image">
          <img src="${imageUrl}" alt="${productData.title}">
        </div>
        <div class="bundle-product-info">
          <h5 class="bundle-product-title">${productData.name}</h5>
          <p class="bundle-product-price">${window.theme.formatMoney(productData.price, window.theme.moneyFormat)}</p>
          <select class="bundle-product-quantity" data-bundle-product-quantity data-index="${index}">
            ${[...Array(10).keys()]
              .map((i) => {
                const quantity = i + 1;
                return `<option value="${quantity}" ${
                  item.quantity === quantity ? 'selected' : ''
                }>${quantity}</option>`;
              })
              .join('')}
          </select>
        </div>
        <button class="bundle-product-remove" data-remove-bundle-item="${index}" aria-label="Remove item">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      `;

      bundleProductsContainer.appendChild(productCard);
    });
  }

  updateUI() {
    // Update total items count
    const bundleTotalItems = document.querySelector('[data-bundle-total-items] span');
    if (bundleTotalItems) {
      bundleTotalItems.textContent = this.totalUnderwearItems + this.totalSwimwearItems;
    }

    // Update products HTML
    this.updateBundleProductsHtml();

    // Update price
    this.updateBundlePrice();

    // Show/hide bundle actions
    const bundleActions = document.querySelector('[data-bundle-actions]');
    if (bundleActions) {
      if (this.totalSwimwearItems + this.totalUnderwearItems > 0) {
        bundleActions.classList.remove('hidden');
      } else {
        bundleActions.classList.add('hidden');
      }
    }
  }

  handleBundleCollapse() {
    const bundleContainer = document.querySelector('[data-bundle-body]');
    if (bundleContainer) {
      bundleContainer.classList.toggle('is-hidden');
    }
  }

  addBundleToCart() {
    if (this.bundleItems.length === 0) return;

    const formData = {
      items: this.bundleItems,
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((data) => {
        if (window.Rebuy?.SmartCart) {
          window.Rebuy.SmartCart.cart.attributes = {
            ...window.Rebuy.SmartCart.cart.attributes,
            pack_builder: true,
          };
        }

        // Also set attribute on Shopify's native cart
        fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              pack_builder: 'true',
            },
          }),
        })
          .then((response) => response.json())
          .then((cartData) => {
            console.log('Cart attributes updated:', cartData.attributes);
          })
          .catch((error) => {
            console.error('Error updating cart attributes:', error);
          });

        // if (window.Rebuy?.SmartCart?.applyDiscount) {
        //   // Check what product types are in the bundle
        //   const hasUnderwear = this.bundleItems.some((item) => item.productType === 'underwear');
        //   const hasSwimwear = this.bundleItems.some((item) => item.productType === 'swimwear');

        //   if (hasUnderwear) {
        //     window.Rebuy.SmartCart.applyDiscount('PACK BUILDER UNDERWEAR');
        //   }
        //   if (hasSwimwear) {
        //     window.Rebuy.SmartCart.applyDiscount('PACK BUILDER SWIMWEAR');
        //   }
        // }
      })
      .catch((error) => {
        console.error('Error adding bundle to cart:', error);
      });
  }

  getDiscountSettings() {
    const discountSettingsJson = document.querySelectorAll('[data-discount-settings-json]');
    return [...discountSettingsJson].map((setting) => {
      return JSON.parse(setting.textContent);
    });
  }

  // Public method to reinitialize (called from filtering/infinite scroll)
  reinitialize() {
    // Update DOM references (in case elements were replaced)
    this.packBuilder = document.querySelector('[data-pack-builder]');

    if (!this.packBuilder) {
      return;
    }

    // Restore the discount text if items exist
    if (this.bundleItems.length > 0) {
      this.recalculateAllDiscounts();
    }

    // Update UI with current state (this will restore the bundle display)
    this.updateUI();
  }

  // Method to completely reset the bundle
  reset() {
    this.totalUnderwearItems = 0;
    this.totalSwimwearItems = 0;
    this.totalBundlePrice = 0;
    this.bundleDiscountAmountUnderwear = 0;
    this.bundleDiscountAmountSwimwear = 0;
    this.bundleItems = [];
    this.resetDiscounts();
    this.updateUI();
  }
}

// Initialize PackBuilder when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.packBuilder = new PackBuilder();
});

// Reinitialize on section load (for theme editor)
document.addEventListener('shopify:section:load', () => {
  if (window.packBuilder) {
    window.packBuilder.reinitialize();
  } else {
    window.packBuilder = new PackBuilder();
  }
});

// Legacy compatibility - create initBundle function for existing code
window.initBundle = () => {
  if (window.packBuilder) {
    window.packBuilder.reinitialize();
  } else {
    window.packBuilder = new PackBuilder();
  }
};

document.addEventListener('rebuy:smartcart.hide', (event) => {
  document.documentElement.removeAttribute('data-scroll-locked');
  document.body.removeAttribute('style');
});
